import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FaVideo, FaVideoSlash, FaMicrophone, FaMicrophoneSlash,
  FaTimes, FaUsers, FaCog, FaExchangeAlt,
  FaEye, FaClock, FaGlobe, FaLock,
  FaGamepad, FaMusic, FaPalette, FaCode, FaUtensils,
  FaRunning, FaGraduationCap, FaComments
} from 'react-icons/fa';
import api from '../utils/api';
import Sidebar from '../components/Sidebar';
import LiveBroadcastView from '../components/live/LiveBroadcastView';

const CATEGORIES = [
  { id: 'chat', label: 'Just Chatting', icon: FaComments, color: '#23cda0' },
  { id: 'gaming', label: 'Gaming', icon: FaGamepad, color: '#6c30ad' },
  { id: 'music', label: 'Music', icon: FaMusic, color: '#47aef0' },
  { id: 'creative', label: 'Creative', icon: FaPalette, color: '#23cda0' },
  { id: 'tech', label: 'Tech', icon: FaCode, color: '#2263f2' },
  { id: 'food', label: 'Food', icon: FaUtensils, color: '#47aef0' },
  { id: 'fitness', label: 'Fitness', icon: FaRunning, color: '#41a3f0' },
  { id: 'education', label: 'Education', icon: FaGraduationCap, color: '#6c30ad' },
];

const GoLive = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState('setup');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('chat');
  const [isVideoOn, setIsVideoOn] = useState(true);
  const [isAudioOn, setIsAudioOn] = useState(true);
  const [isPublic, setIsPublic] = useState(true);
  const [stream, setStream] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [viewerCount, setViewerCount] = useState(0);
  const [likes, setLikes] = useState(0);
  const [showHearts, setShowHearts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [duration, setDuration] = useState(0);
  const [pinnedComment, setPinnedComment] = useState(null);
  const [milestone, setMilestone] = useState(null);
  const prevViewerRef = useRef(0);
  const commentsEndRef = useRef(null);

  const MILESTONES = [10, 25, 50, 100, 250, 500, 1000];
  const MILESTONE_MESSAGES = {
    10: 'First 10 viewers!', 25: '25 viewers!', 50: 'You hit 50 viewers!',
    100: '100 viewers! You\'re on fire!', 250: '250 viewers! Amazing!',
    500: '500 viewers! Incredible!', 1000: '1K viewers! Legendary!',
  };

  // Check for existing active stream on mount
  useEffect(() => {
    const checkActiveStream = async () => {
      try {
        const res = await api.get('/live/active');
        const currentUser = JSON.parse(sessionStorage.getItem('user') || '{}');
        const myStream = res.data.find(s => s.broadcaster_id === currentUser.id && s.status === 'live');
        if (myStream) {
          setStream(myStream);
          setTitle(myStream.title || '');
          setDescription(myStream.description || '');
          setStep('live');
        }
      } catch (err) { console.error('Failed to check active stream', err); }
    };
    checkActiveStream();
  }, []);

  // Detect viewer milestones
  useEffect(() => {
    if (step !== 'live') return;
    const prev = prevViewerRef.current;
    for (const m of MILESTONES) {
      if (prev < m && viewerCount >= m) {
        setMilestone(m);
        setTimeout(() => setMilestone(null), 5000);
        break;
      }
    }
    prevViewerRef.current = viewerCount;
  }, [viewerCount, step]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    commentsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [comments]);

  // Timer when live
  useEffect(() => {
    if (step !== 'live') return;
    const t = setInterval(() => setDuration(d => d + 1), 1000);
    return () => clearInterval(t);
  }, [step]);

  // Poll for updates when live
  useEffect(() => {
    if (step !== 'live' || !stream) return;
    const interval = setInterval(async () => {
      try {
        const res = await api.get(`/live/${stream.id}`);
        setComments(res.data.comments || []);
        setLikes(res.data.likes || 0);
        setViewerCount(res.data.stream.viewer_count || 0);
        setPinnedComment(res.data.pinned_comment || null);
      } catch (err) { console.error('Failed to poll live updates', err); }
    }, 1500);
    return () => clearInterval(interval);
  }, [step, stream]);

  // Simulate hearts
  useEffect(() => {
    if (step !== 'live') return;
    const interval = setInterval(() => {
      if (Math.random() > 0.6) {
        const id = Date.now();
        setShowHearts(prev => [...prev, id]);
        setTimeout(() => setShowHearts(prev => prev.filter(h => h !== id)), 2000);
      }
    }, 2500);
    return () => clearInterval(interval);
  }, [step]);

  const formatDuration = (s) => {
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    return h > 0
      ? `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`
      : `${m}:${String(sec).padStart(2, '0')}`;
  };

  const handleStartLive = async () => {
    if (!title.trim()) return;
    setIsLoading(true);
    try {
      const res = await api.post('/live/start', { title, description });
      setStream(res.data);
      setStep('live');
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to start live stream');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEndLive = async () => {
    if (!stream) return;
    try { await api.post(`/live/${stream.id}/end`); } catch (err) { console.error('Failed to end stream', err); }
    navigate('/dashboard');
  };

  const handleSendComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim() || !stream) return;
    const content = newComment;
    setNewComment('');
    try {
      const res = await api.post(`/live/${stream.id}/comment`, { content });
      setComments(prev => [...prev, res.data]);
    } catch (err) { console.error('Failed to send comment', err); }
  };

  const handlePinComment = async (commentId) => {
    if (!stream) return;
    try {
      const res = await api.post(`/live/${stream.id}/pin/${commentId}`);
      setPinnedComment(res.data.pinned_comment);
    } catch (err) { console.error('Failed to pin comment', err); }
  };

  const handleUnpinComment = async () => {
    if (!stream) return;
    try {
      await api.post(`/live/${stream.id}/unpin`);
      setPinnedComment(null);
    } catch (err) { console.error('Failed to unpin comment', err); }
  };

  const selectedCat = CATEGORIES.find(c => c.id === category);

  // ========== SETUP SCREEN ==========
  if (step === 'setup') {
    return (
      <div className="min-h-screen bg-[#0B0F14]">
        <div className="grid grid-cols-1 md:grid-cols-12">
          <Sidebar />
          <main className="col-span-12 md:col-span-10" data-testid="go-live-page">
            <div className="min-h-screen flex flex-col">
              {/* Header */}
              <div className="px-6 py-4 border-b border-white/5 flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                  <h1 className="text-xl font-outfit font-bold text-white tracking-tight font-arthaus">PointLive</h1>
                </div>
                <button
                  onClick={() => navigate('/dashboard')}
                  className="text-white/40 hover:text-white p-2 rounded-lg hover:bg-white/5 transition-colors"
                  data-testid="close-go-live"
                >
                  <FaTimes size={18} />
                </button>
              </div>

              {/* Main Content */}
              <div className="flex-1 p-6 lg:p-10">
                <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-5 gap-8">

                  {/* Left: Camera Preview */}
                  <div className="lg:col-span-3">
                    <div className="relative aspect-video bg-[#161B22] rounded-2xl overflow-hidden border border-white/5" data-testid="camera-preview">
                      {/* Animated gradient background */}
                      <div className="absolute inset-0">
                        <div className="absolute inset-0 bg-gradient-to-br from-[#23cda0]/20 via-transparent to-[#6c30ad]/10" />
                        {isVideoOn && (
                          <motion.div
                            animate={{ opacity: [0.3, 0.6, 0.3] }}
                            transition={{ duration: 4, repeat: Infinity }}
                            className="absolute inset-0 bg-gradient-to-t from-[#0B0F14] via-transparent to-transparent"
                          />
                        )}
                      </div>

                      {/* Center icon */}
                      <div className="absolute inset-0 flex items-center justify-center">
                        {isVideoOn ? (
                          <div className="text-center">
                            <motion.div
                              animate={{ scale: [1, 1.05, 1] }}
                              transition={{ duration: 2, repeat: Infinity }}
                              className="w-12 h-12 sm:w-20 sm:h-20 rounded-2xl bg-gradient-to-br from-[#23cda0] via-[#2263f2] to-[#6c30ad] flex items-center justify-center mx-auto mb-2 sm:mb-3 shadow-lg shadow-[#2263f2]/30"
                            >
                              <FaVideo className="text-white text-lg sm:text-2xl" />
                            </motion.div>
                            <p className="text-white/60 text-sm font-medium">Camera Preview</p>
                            <p className="text-white/30 text-xs mt-1"></p>
                          </div>
                        ) : (
                          <div className="text-center">
                            <div className="w-12 h-12 sm:w-20 sm:h-20 rounded-2xl bg-white/5 flex items-center justify-center mx-auto mb-2 sm:mb-3">
                              <FaVideoSlash className="text-white/30 text-lg sm:text-2xl" />
                            </div>
                            <p className="text-white/40 text-sm">Camera Off</p>
                          </div>
                        )}
                      </div>

                      {/* Bottom controls overlay */}
                      <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/60 to-transparent">
                        <div className="flex items-center justify-center space-x-3">
                          <button
                            onClick={() => setIsVideoOn(!isVideoOn)}
                            data-testid="toggle-video"
                            className={`p-3 rounded-xl transition-all ${
                              isVideoOn
                                ? 'bg-white/10 backdrop-blur-sm text-white hover:bg-white/20'
                                : 'bg-red-500/90 text-white hover:bg-red-600'
                            }`}
                          >
                            {isVideoOn ? <FaVideo size={16} /> : <FaVideoSlash size={16} />}
                          </button>
                          <button
                            onClick={() => setIsAudioOn(!isAudioOn)}
                            data-testid="toggle-audio"
                            className={`p-3 rounded-xl transition-all ${
                              isAudioOn
                                ? 'bg-white/10 backdrop-blur-sm text-white hover:bg-white/20'
                                : 'bg-red-500/90 text-white hover:bg-red-600'
                            }`}
                          >
                            {isAudioOn ? <FaMicrophone size={16} /> : <FaMicrophoneSlash size={16} />}
                          </button>
                          <button className="p-3 rounded-xl bg-white/10 backdrop-blur-sm text-white hover:bg-white/20 transition-all" data-testid="flip-camera">
                            <FaExchangeAlt size={16} />
                          </button>
                        </div>
                      </div>

                      {/* Status badges */}
                      <div className="absolute top-4 left-4 flex items-center space-x-2">
                        <span className={`px-2.5 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wider ${
                          isVideoOn ? 'bg-[#23cda0]/20 text-[#23cda0]' : 'bg-red-500/20 text-red-400'
                        }`}>
                          {isVideoOn ? 'Video On' : 'Video Off'}
                        </span>
                        <span className={`px-2.5 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wider ${
                          isAudioOn ? 'bg-[#23cda0]/20 text-[#23cda0]' : 'bg-red-500/20 text-red-400'
                        }`}>
                          {isAudioOn ? 'Mic On' : 'Mic Off'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Right: Stream Settings */}
                  <div className="lg:col-span-2 space-y-5">
                    {/* Title */}
                    <div>
                      <label className="block text-white/50 text-xs font-bold uppercase tracking-wider mb-2">Stream Title *</label>
                      <input
                        type="text"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder="What's your stream about?"
                        className="w-full bg-[#161B22] border border-white/10 text-white rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#23cda0] focus:ring-1 focus:ring-[#23cda0]/30 placeholder:text-white/20 transition-all"
                        maxLength={100}
                        data-testid="stream-title-input"
                      />
                      <p className="text-white/20 text-xs mt-1 text-right">{title.length}/100</p>
                    </div>

                    {/* Description */}
                    <div>
                      <label className="block text-white/50 text-xs font-bold uppercase tracking-wider mb-2">Description</label>
                      <textarea
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder="Tell viewers what to expect..."
                        className="w-full bg-[#161B22] border border-white/10 text-white rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#23cda0] focus:ring-1 focus:ring-[#23cda0]/30 placeholder:text-white/20 transition-all resize-none"
                        rows={3}
                        maxLength={280}
                        data-testid="stream-description-input"
                      />
                    </div>

                    {/* Category */}
                    <div>
                      <label className="block text-white/50 text-xs font-bold uppercase tracking-wider mb-2">Category</label>
                      <div className="grid grid-cols-2 gap-2" data-testid="category-picker">
                        {CATEGORIES.map(cat => {
                          const Icon = cat.icon;
                          const active = category === cat.id;
                          return (
                            <button
                              key={cat.id}
                              onClick={() => setCategory(cat.id)}
                              data-testid={`category-${cat.id}`}
                              className={`flex items-center space-x-2 px-3 py-2.5 rounded-xl text-xs font-medium transition-all ${
                                active
                                  ? 'bg-white/10 border border-white/20 text-white'
                                  : 'bg-[#161B22] border border-white/5 text-white/40 hover:text-white/70 hover:border-white/10'
                              }`}
                            >
                              <Icon size={12} style={{ color: active ? cat.color : undefined }} />
                              <span>{cat.label}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Privacy Toggle */}
                    <div className="flex items-center justify-between bg-[#161B22] border border-white/5 rounded-xl px-4 py-3">
                      <div className="flex items-center space-x-2">
                        {isPublic ? <FaGlobe className="text-[#23cda0]" size={14} /> : <FaLock className="text-white/40" size={14} />}
                        <span className="text-sm text-white/70">{isPublic ? 'Public' : 'Followers Only'}</span>
                      </div>
                      <button
                        onClick={() => setIsPublic(!isPublic)}
                        data-testid="toggle-privacy"
                        className={`w-10 h-5 rounded-full transition-all relative ${isPublic ? 'bg-[#23cda0]' : 'bg-white/20'}`}
                      >
                        <div className={`w-4 h-4 rounded-full bg-white absolute top-0.5 transition-all shadow ${isPublic ? 'left-5.5 translate-x-0.5' : 'left-0.5'}`} />
                      </button>
                    </div>

                    {/* PointLive Button */}
                    <button
                      onClick={handleStartLive}
                      disabled={!title.trim() || isLoading}
                      data-testid="go-live-btn"
                      className={`w-full py-4 rounded-xl font-bold text-sm uppercase tracking-wider transition-all ${
                        title.trim() && !isLoading
                          ? 'bg-gradient-to-r from-[#23cda0] to-[#2263f2] text-white hover:from-[#1db890] hover:to-[#1b56d9] shadow-lg shadow-[#23cda0]/20 hover:scale-[1.02] active:scale-95'
                          : 'bg-white/5 text-white/20 cursor-not-allowed'
                      }`}
                    >
                      {isLoading ? (
                        <span className="flex items-center justify-center space-x-2">
                          <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: 'linear' }} className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full" />
                          <span>Connecting...</span>
                        </span>
                      ) : (
                        <span className="flex items-center justify-center space-x-2">
                          <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                          <span className="font-arthaus">PointLive</span>
                        </span>
                      )}
                    </button>

                    <p className="text-white/20 text-xs text-center">
                      Your followers will be notified when you go live
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  // ========== LIVE BROADCASTING SCREEN ==========
  return (
    <LiveBroadcastView
      stream={stream} isVideoOn={isVideoOn} setIsVideoOn={setIsVideoOn}
      isAudioOn={isAudioOn} setIsAudioOn={setIsAudioOn}
      duration={duration} viewerCount={viewerCount} likes={likes}
      milestone={milestone} showHearts={showHearts}
      comments={comments} newComment={newComment} setNewComment={setNewComment}
      pinnedComment={pinnedComment}
      handleEndLive={handleEndLive} handleSendComment={handleSendComment}
      handlePinComment={handlePinComment} handleUnpinComment={handleUnpinComment}
      commentsEndRef={commentsEndRef} selectedCat={selectedCat}
    />
  );
};

export default GoLive;
